import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva } from "class-variance-authority";
import { LoaderCircle } from "lucide-react";
import { cn } from "@/lib/utils";

/* Pulsante del gestionale (pacchetto del 29-10, ui/button.tsx). Un solo «primary» per schermata.
   Due differenze volute dal pacchetto, per non cambiare il comportamento di oggi:
   · «disabled» resta l'attributo vero (i form e i test ci contano); ha l'aspetto «non disponibile»
     del pacchetto, non più l'opacità. aria-disabled lo prende chi lo passa a mano.
   · il «type» non diventa «button» da solo: dentro un form un <Button> senza type invia, come prima.
   I nomi vecchi delle varianti (default, outline, destructive, ghost, link) restano alias. */
const varianti = cva(
  [
    "inline-flex shrink-0 items-center justify-center gap-2 whitespace-nowrap text-body font-semibold",
    "transition-colors duration-150 ease-enter",
    "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand",
    "disabled:cursor-not-allowed aria-disabled:cursor-not-allowed aria-busy:cursor-progress",
    "[&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-[18px]",
  ],
  {
    variants: {
      variant: {
        // blu pieno: il passo principale
        primary:
          "bg-brand font-bold text-on-brand hover:bg-brand-hover disabled:bg-line disabled:text-ink-2 aria-disabled:bg-line aria-disabled:text-ink-2 aria-busy:bg-brand aria-busy:text-on-brand",
        // bianco con bordo: tutto il resto
        secondary:
          "border border-line-input bg-surface text-ink hover:bg-surface-2 aria-pressed:border-ink-3 aria-pressed:bg-surface-3 disabled:border-line disabled:bg-surface-3 disabled:text-ink-2 aria-disabled:border-line aria-disabled:bg-surface-3 aria-disabled:text-ink-2",
        // solo testo: azioni secondarie e link
        text: "min-h-10 px-1 text-brand-ink underline underline-offset-[3px] hover:decoration-2 disabled:text-ink-2",
        // pericolo: bordo rosso, mai pieno
        danger: "border border-danger bg-surface text-danger-ink hover:bg-danger-bg disabled:border-line disabled:text-ink-2",
        // sul blu del menu e delle testate: colori fissi, uguali nel tema chiaro e nello scuro
        onBlue: "bg-nav-button font-bold text-nav-button-ink hover:bg-nav-button-hover",
        // senza bordo né fondo, per le icone e le azioni dentro una riga
        ghost: "text-ink hover:bg-surface-2 disabled:text-ink-3 disabled:opacity-60",
      },
      size: {
        // Sotto i 768 px si tocca col dito: 44 almeno (c32, la regola del pacchetto che il guscio già rispetta).
        sm: "h-[38px] rounded-sm px-3 max-md:h-11", // nelle righe
        md: "h-[42px] rounded-sm px-4 max-md:h-11", // standard
        lg: "h-[46px] rounded-md px-4", // finestre
        xl: "h-[52px] rounded-md px-5 text-base", // accesso
        touch: "h-12 rounded-md px-4", // telefono, 48
        mobile: "h-[54px] w-full rounded-lg px-5 text-base", // telefono, principale
        icon: "size-11 rounded-md", // 44, sempre con aria-label
      },
    },
    compoundVariants: [{ variant: "text", className: "h-auto" }],
    defaultVariants: { variant: "primary", size: "md" },
  },
);

const ALIAS_VARIANTE = { default: "primary", outline: "secondary", destructive: "danger", link: "text" };
const ALIAS_MISURA = { default: "md" };
const buttonVariants = ({ variant, size } = {}) =>
  varianti({ variant: ALIAS_VARIANTE[variant] || variant, size: ALIAS_MISURA[size] || size });

const Button = React.forwardRef(
  ({ className, variant, size, asChild = false, loading = false, loadingText, children, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        ref={ref}
        data-slot="button"
        className={cn(buttonVariants({ variant, size }), className)}
        aria-busy={loading || undefined}
        {...props}
        disabled={asChild ? undefined : props.disabled || loading}
      >
        {loading && !asChild ? (
          <>
            <LoaderCircle className="animate-spin motion-reduce:animate-none" aria-hidden="true" />
            {loadingText ?? children}
          </>
        ) : (
          children
        )}
      </Comp>
    );
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
